"use client"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useState, useRef, useEffect } from "react"
import { Menu, X, User, Crown, ShieldCheck, Globe } from "lucide-react"
import { OpenCallSearchBar } from "@/components/OpenCallSearchBar"
import { useLocale } from "@/components/LocaleProvider"
import { locales, localeNames, type Locale } from "@/lib/i18n"
import { cn } from "@/lib/utils"
import { signOut, useSession } from "next-auth/react"

const navLinkKeys = [
  { href: "/directory", key: "nav.directory" },
  { href: "/top-rated", key: "nav.topRated" },
  { href: "/magazine", key: "nav.magazine" },
]

const ADMIN_EMAIL = process.env.NEXT_PUBLIC_ADMIN_EMAIL ?? ""

export function Header() {
  const pathname = usePathname()
  const { data: session } = useSession()
  const [menuOpen, setMenuOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const { locale, setLocale, t } = useLocale()
  const userMenuRef = useRef<HTMLDivElement>(null)

  const isAdmin = !!ADMIN_EMAIL && session?.user?.email === ADMIN_EMAIL
  const nextLocale = locales[(locales.indexOf(locale) + 1) % locales.length]
  const isOpenCallsPage = pathname === "/open-calls"

  // Close user menu on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    if (userMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [userMenuOpen])

  // Close menus on route change
  useEffect(() => {
    setUserMenuOpen(false)
    setMenuOpen(false)
  }, [pathname])

  return (
    <header className="sticky top-0 z-50 bg-[var(--background)]/95 backdrop-blur-md">
      {/* Main navigation */}
      <div className="border-b border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[3.75rem]">
            {/* Wordmark logo — typographic, no icon */}
            <Link
              href="/"
              className="flex items-baseline gap-0 hover:opacity-70 transition-opacity flex-shrink-0"
            >
              <span className="font-editorial text-lg tracking-tight text-[var(--foreground)]">
                Rate My Artist Residency
              </span>
            </Link>

            {/* Desktop nav — right-aligned, gallery-minimal */}
            <nav className="hidden lg:flex items-center gap-0.5">
              <Link
                href="/open-calls"
                className="px-3 py-1.5 text-[13px] tracking-wide text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
              >
                Open Calls
              </Link>
              {navLinkKeys.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={cn(
                    "px-3 py-1.5 text-[13px] tracking-wide transition-colors",
                    pathname === link.href
                      ? "text-[var(--foreground)] font-medium"
                      : "text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                  )}
                >
                  {t(link.key)}
                </Link>
              ))}

              <span className="w-px h-4 bg-[var(--border)] mx-2" />

              <Link
                href="/submit"
                className="px-3 py-1.5 text-[13px] tracking-wide text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
              >
                {t("nav.submitResidency")}
              </Link>

              <button
                onClick={() => setLocale(nextLocale as Locale)}
                className="p-2 hover:bg-[var(--secondary)] rounded-full transition-colors ml-1"
                title={`Switch to ${localeNames[nextLocale]}`}
                aria-label={`Switch to ${localeNames[nextLocale]}`}
              >
                <Globe size={15} className="text-[var(--muted-foreground)]" />
              </button>

              {/* User menu pill — refined */}
              <div className="relative ml-1" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className={cn(
                    "flex items-center gap-2 border border-[var(--border)] rounded-full pl-2.5 pr-1 py-0.5 hover:shadow-sm transition-all cursor-pointer",
                    userMenuOpen && "shadow-sm border-[var(--muted-foreground)]"
                  )}
                >
                  <Menu size={14} className="text-[var(--muted-foreground)]" />
                  <div className={cn(
                    "w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium",
                    session ? "bg-[var(--primary)] text-white" : "bg-[var(--muted)] text-[var(--muted-foreground)]"
                  )}>
                    {session ? (
                      session.user?.name?.charAt(0).toUpperCase() ?? "U"
                    ) : (
                      <User size={13} />
                    )}
                  </div>
                </button>

                {/* Dropdown */}
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-lg border border-[var(--border)] shadow-lg py-1 z-50 animate-dropdown-in">
                    {session ? (
                      <>
                        <Link
                          href="/account"
                          className="block px-4 py-2 text-[13px] font-medium text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors"
                        >
                          {t("nav.myAccount")}
                        </Link>
                        <Link
                          href="/programs"
                          className="block px-4 py-2 text-[13px] text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors"
                        >
                          {t("nav.myResidencies")}
                        </Link>
                        {/* Admins see the same menu as program admins — "My
                            Residencies" (/programs) already redirects them to
                            /admin/residencies — plus the Admin overview. */}
                        {isAdmin && (
                          <Link
                            href="/admin"
                            className="flex items-center gap-2 px-4 py-2 text-[13px] text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors"
                          >
                            <ShieldCheck size={13} />
                            {t("nav.admin")}
                          </Link>
                        )}
                        <div className="my-1 border-t border-[var(--border)]" />
                        <Link
                          href="/pro"
                          className="flex items-center gap-2 px-4 py-2 text-[13px] text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors"
                        >
                          <Crown size={13} className="text-[var(--gold)]" />
                          {t("nav.upgradePro")}
                        </Link>
                        <div className="my-1 border-t border-[var(--border)]" />
                        <button
                          onClick={() => signOut({ callbackUrl: "/" })}
                          className="w-full text-left px-4 py-2 text-[13px] text-[var(--muted-foreground)] hover:bg-[var(--secondary)] transition-colors cursor-pointer"
                        >
                          {t("nav.signOut")}
                        </button>
                        <div className="my-1 border-t border-[var(--border)]" />
                        <Link
                          href="/about"
                          className="block px-4 py-2 text-[13px] text-[var(--muted-foreground)] hover:bg-[var(--secondary)] transition-colors"
                        >
                          {t("nav.about")}
                        </Link>
                      </>
                    ) : (
                      <>
                        <Link
                          href="/login"
                          className="block px-4 py-2 text-[13px] font-medium text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors"
                        >
                          {t("nav.signIn")}
                        </Link>
                        <Link
                          href="/register"
                          className="block px-4 py-2 text-[13px] text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors"
                        >
                          {t("nav.joinFree")}
                        </Link>
                        <div className="my-1 border-t border-[var(--border)]" />
                        <Link
                          href="/pro"
                          className="flex items-center gap-2 px-4 py-2 text-[13px] text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors"
                        >
                          <Crown size={13} className="text-[var(--gold)]" />
                          {t("nav.upgradePro")}
                        </Link>
                        <div className="my-1 border-t border-[var(--border)]" />
                        <Link
                          href="/submit"
                          className="block px-4 py-2 text-[13px] text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors"
                        >
                          {t("nav.submitResidency")}
                        </Link>
                        <div className="my-1 border-t border-[var(--border)]" />
                        <Link
                          href="/about"
                          className="block px-4 py-2 text-[13px] text-[var(--muted-foreground)] hover:bg-[var(--secondary)] transition-colors"
                        >
                          {t("nav.about")}
                        </Link>
                      </>
                    )}
                  </div>
                )}
              </div>
            </nav>

            {/* Mobile hamburger */}
            <button
              className="lg:hidden p-2 rounded-md hover:bg-[var(--secondary)] transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>

          {/* Mobile menu */}
          {menuOpen && (
            <div className="lg:hidden pb-4 border-t border-[var(--border)] pt-3 animate-slide-down">
              <div className="space-y-0.5">
                <Link
                  href="/open-calls"
                  onClick={() => setMenuOpen(false)}
                  className="block px-3 py-2.5 text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
                >
                  Open Calls
                </Link>
                {navLinkKeys.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMenuOpen(false)}
                    className={cn(
                      "block px-3 py-2.5 text-sm transition-colors",
                      pathname === link.href
                        ? "text-[var(--foreground)] font-medium"
                        : "text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                    )}
                  >
                    {t(link.key)}
                  </Link>
                ))}
                <Link
                  href="/about"
                  onClick={() => setMenuOpen(false)}
                  className={cn(
                    "block px-3 py-2.5 text-sm transition-colors",
                    pathname === "/about"
                      ? "text-[var(--foreground)] font-medium"
                      : "text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                  )}
                >
                  {t("nav.about")}
                </Link>
              </div>

              <div className="my-3 border-t border-[var(--border)]" />

              <div className="space-y-0.5">
                <Link
                  href="/submit"
                  onClick={() => setMenuOpen(false)}
                  className="block px-3 py-2.5 text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
                >
                  {t("nav.submitResidency")}
                </Link>
              </div>

              <div className="my-3 border-t border-[var(--border)]" />

              <div className="space-y-0.5">
                {session ? (
                  <>
                    <Link
                      href="/account"
                      onClick={() => setMenuOpen(false)}
                      className="block px-3 py-2.5 text-sm font-medium text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors"
                    >
                      {t("nav.myAccount")}
                    </Link>
                    <Link
                      href="/programs"
                      onClick={() => setMenuOpen(false)}
                      className="block px-3 py-2.5 text-sm text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors"
                    >
                      {t("nav.myResidencies")}
                    </Link>
                    {isAdmin && (
                      <Link
                        href="/admin"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-2 px-3 py-2.5 text-sm text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors"
                      >
                        <ShieldCheck size={14} />
                        {t("nav.admin")}
                      </Link>
                    )}
                    <Link
                      href="/pro"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2.5 text-sm text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors"
                    >
                      <Crown size={13} className="text-[var(--gold)]" />
                      {t("nav.upgradePro")}
                    </Link>
                    <div className="my-3 border-t border-[var(--border)]" />
                    <button
                      onClick={() => { signOut({ callbackUrl: "/" }); setMenuOpen(false) }}
                      className="w-full text-left px-3 py-2.5 text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
                    >
                      {t("nav.signOut")}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      href="/login"
                      onClick={() => setMenuOpen(false)}
                      className="block px-3 py-2.5 text-sm font-medium text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors"
                    >
                      {t("nav.signIn")}
                    </Link>
                    <Link
                      href="/register"
                      onClick={() => setMenuOpen(false)}
                      className="block px-3 py-2.5 text-sm text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors"
                    >
                      {t("nav.joinFree")}
                    </Link>
                    <Link
                      href="/pro"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2.5 text-sm text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors"
                    >
                      <Crown size={13} className="text-[var(--gold)]" />
                      {t("nav.upgradePro")}
                    </Link>
                  </>
                )}
              </div>

              <div className="my-3 border-t border-[var(--border)]" />

              <button
                onClick={() => { setLocale(nextLocale as Locale); setMenuOpen(false) }}
                className="flex items-center gap-2 px-3 py-2.5 text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors w-full text-left"
              >
                <Globe size={14} />
                {localeNames[nextLocale]}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Open Calls page search bar — explicitly searches open calls
          (opportunities with deadlines), not the directory of residency
          organizations. The homepage renders its own search bar inline,
          after the globe hero. */}
      {isOpenCallsPage && (
        <div className="border-b border-[var(--border)] bg-[var(--background)]/95 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <OpenCallSearchBar mode="openCalls" label="Open calls · search opportunities by discipline, region, funding" />
          </div>
        </div>
      )}
    </header>
  )
}
